<template>
  <LabelAndHint
    v-slot="{ inputId }"
    v-bind="{ ...props, error: props.error || invalid, errorMessages: props.errorMessages || errorMessages }"
  >
    <label ref="checkboxElement" class="app-checkbox" :class="{ checked: isChecked, disabled }">
      <input
        :id="inputId"
        type="checkbox"
        :checked="isChecked"
        :disabled="disabled"
        @change="onChange"
        @blur="onBlur"
      />
      <slot />
    </label>
  </LabelAndHint>
</template>

<script setup lang="ts">
import { computed, nextTick, ref, watch } from 'vue'

import type { ValidationRuleObject } from '@staff/shared'

import { useInputValidation } from '../../composables'
import LabelAndHint from '../AppTextField/LabelAndHint.vue'

const emit = defineEmits<{
  (e: 'input', v: boolean): void
  (e: 'update:modelValue', v: boolean): void
  (e: 'blur'): void
}>()

const props = withDefaults(
  defineProps<{
    id?: string | number

    label?: string
    hint?: string | string[]
    hideDetails?: boolean

    error?: boolean
    errorMessages?: string | string[]

    // For shared components to work in both vue 2.7 and vue 3 we support both `value` and `modelValue`.
    value?: boolean | string | null
    modelValue?: boolean | string | null

    disabled?: boolean

    rules?: (ValidationRuleObject | null)[]
    validationOptions?: {
      validateOnModelChange?: boolean
    }
  }>(),
  {
    // Keep these `undefined` defaults: without them Vue's boolean casting turns an absent `modelValue` into
    // `false`, and `modelValue ?? value` below then ignores Vue 2's v-model (`value`).
    value: undefined,
    modelValue: undefined,
  },
)

const checkboxElement = ref<HTMLElement | null | undefined>(undefined)
const currentValue = computed(() => props.modelValue ?? props.value)
const isChecked = computed(() => !!currentValue.value)
const { invalid, errorMessages, validate } = useInputValidation(
  () => currentValue.value,
  () => props.rules,
  checkboxElement,
)

const onChange = (event: Event) => {
  if (props.disabled) return

  const checked = (event.target as HTMLInputElement).checked
  emit('input', checked)
  emit('update:modelValue', checked)

  if ((props.rules?.length ?? 0) > 0) {
    nextTick(() => validate())
  }
}

const onBlur = () => {
  emit('blur')

  if ((props.rules?.length ?? 0) > 0) {
    nextTick(() => validate())
  }
}

watch(
  () => currentValue.value,
  () => {
    if (invalid.value || props.validationOptions?.validateOnModelChange) {
      nextTick(() => validate())
    }
  },
)
</script>

<style lang="scss" scoped>
.app-checkbox {
  cursor: pointer;

  display: flex;
  align-items: baseline;
  gap: 10px;

  color: var(--color-fg-default);

  padding: 6px;

  font-size: 16px;
  font-weight: 400;
  line-height: 140%;
  text-align: center;

  input {
    cursor: pointer;
    transform: scale(1.3);
  }

  &.disabled {
    cursor: not-allowed;
    color: var(--color-fg-disabled);

    input {
      cursor: not-allowed;
    }
  }
}
</style>
